import { useState } from "react";
import { useParams } from "react-router-dom";
import { BadgeCheck, MapPin, Globe, Instagram } from "lucide-react";
import { GoldButton } from "@/components/site/ui";
import { useFetch } from "@/lib/useFetch";
import { useSeo } from "@/lib/seo";
import { useLead } from "@/context/LeadContext";

const TABS = ["About", "Products", "Media", "Updates"];

export default function BrandPublic() {
    const { slug } = useParams();
    const { openLead } = useLead();
    const { data: b, loading } = useFetch(`/brand/public/${slug}`);
    const [tab, setTab] = useState("About");
    useSeo(b ? { title: `${b.name} | SuDigi Brand`, description: b.about || "" , jsonLd: { "@context": "https://schema.org", "@type": "Organization", name: b.name, description: b.about, areaServed: "IN" } } : { title: "Brand | SuDigi" });

    if (loading) return <div className="min-h-[60vh] grid place-items-center text-cream-soft">Loading…</div>;
    if (!b) return <div className="min-h-[60vh] grid place-items-center text-cream-soft">Brand not found.</div>;

    return (
        <section className="bg-teal-900 min-h-[70vh]">
            <div className="h-48 sm:h-60 bg-teal-800 relative overflow-hidden">
                {b.image && <img src={b.image} alt={b.name} className="w-full h-full object-cover opacity-70" />}
                <div className="absolute inset-0 bg-gradient-to-t from-teal-900 to-transparent" />
            </div>
            <div className="container-x -mt-16 relative">
                <div className="flex flex-wrap items-end gap-4">
                    <div className="w-24 h-24 rounded-2xl bg-teal-700 border border-brandborder overflow-hidden grid place-items-center">
                        {b.image ? <img src={b.image} alt={b.name} className="w-full h-full object-cover" /> : <span className="font-serif text-3xl text-gold">{b.name.charAt(0)}</span>}
                    </div>
                    <div className="flex-1">
                        <div className="flex items-center gap-2"><h1 className="font-serif text-3xl text-cream">{b.name}</h1>{b.verified && <BadgeCheck className="w-5 h-5 text-growth" />}</div>
                        <div className="flex flex-wrap gap-4 text-sm text-cream-soft mt-2">
                            {b.industry && <span>{b.industry}</span>}
                            {b.location && <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {b.location}</span>}
                            {b.website && <a href={b.website} target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:text-gold"><Globe className="w-4 h-4" /> Website</a>}
                            {b.instagram && <a href={b.instagram} target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:text-gold"><Instagram className="w-4 h-4" /> Instagram</a>}
                        </div>
                    </div>
                    <GoldButton onClick={() => openLead(b.name)} data-testid="brand-connect">Connect With Brand</GoldButton>
                </div>

                <div className="flex gap-2 mt-8 border-b border-brandborder overflow-x-auto">
                    {TABS.map((t) => <button key={t} onClick={() => setTab(t)} className={`px-4 py-3 text-sm font-medium whitespace-nowrap ${tab === t ? "text-gold border-b-2 border-gold" : "text-cream-soft"}`} data-testid={`brand-tab-${t.toLowerCase()}`}>{t}</button>)}
                </div>
                <div className="py-8">
                    {tab === "About" && <p className="text-cream-soft max-w-2xl leading-relaxed">{b.about || "No description yet."}</p>}
                    {tab === "Products" && (
                        (b.products || []).length ? <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{b.products.map((p, i) => <div key={i} className="card-teal rounded-xl p-5"><div className="font-semibold text-cream">{p.name}</div><div className="text-sm text-cream-soft mt-1">{p.detail}</div></div>)}</div> : <Empty />
                    )}
                    {tab === "Media" && ((b.media || []).length ? <div className="grid sm:grid-cols-3 gap-4">{b.media.map((m, i) => <img key={i} src={m} alt="" className="rounded-xl h-40 w-full object-cover" />)}</div> : <Empty />)}
                    {tab === "Updates" && ((b.updates || []).length ? <div className="space-y-3 max-w-2xl">{b.updates.map((u, i) => <div key={i} className="card-teal rounded-xl p-4 text-cream-soft text-sm">{u.text}</div>)}</div> : <Empty />)}
                </div>
            </div>
        </section>
    );
}
function Empty() { return <p className="text-cream-soft/60">Nothing here yet.</p>; }
